import clsx from "clsx";
import { motion } from "framer-motion";
import PropTypes from "prop-types";
import { Children, cloneElement, isValidElement, useState } from "react";
import Skeleton from "react-loading-skeleton";

import { RadioGroup } from "@transfr-inc/dashboard-components/forms";

import { currencyFormat } from "@lib/text.helpers";
import { useBudgetCategories, useBudgetCategoryOptions } from "../api";

import "./budget-category.view.scss";

const defaultGetOptionAmount = (option) => option.amount;

export const BudgetCategoryView = ({
  budgetCategoryCode,
  children,
  className,
  getOptionAmount = defaultGetOptionAmount,
  isSelecting,
  onChoiceChange,
  renderOptionContent,
  selected,
}) => {
  const imageUrl = `/images/budget/${budgetCategoryCode.toLowerCase()}.png`;

  const [loading, setLoading] = useState(true);

  const { data: budgetCategories } = useBudgetCategories();
  const category = budgetCategories?.[budgetCategoryCode];

  const { data: budgetCategoryOptions } = useBudgetCategoryOptions(
    category?.id,
  );

  const handleChoiceChange = (value) => {
    onChoiceChange?.(Number(value));
  };

  const defaultRenderOptionContent = (option) => (
    <div className="budget-option-content">
      <div className="budget-option-label">
        {option.label} ({currencyFormat(getOptionAmount(option))}/mo)
      </div>
      {option.description && (
        <div className="budget-option-description">{option.description}</div>
      )}
    </div>
  );

  const renderPrelude = () => {
    const preludeChild = Children.toArray(children).find(
      (child) =>
        isValidElement(child) && child.type === BudgetCategoryView.Prelude,
    );

    return preludeChild ? cloneElement(preludeChild) : undefined;
  };

  return (
    <div className={clsx("budget-category-view", className)}>
      <div className="budget-category-view-header">
        <h3>{category?.label}</h3>
        <motion.div
          className="budget-image"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.img
            aria-hidden
            src={imageUrl}
            onLoad={() => setLoading(false)}
            alt={`${category?.code} budget`}
            animate={{ scale: loading ? 0 : 1 }}
            className={clsx(loading && "loading")}
          />
          {loading && (
            <Skeleton circle containerClassName="tx-skeleton-container" />
          )}
        </motion.div>
      </div>

      <div className="budget-category-view-prelude">
        {renderPrelude()}

        {category?.description && (
          <div className="budget-category-view-prelude-description">
            {category.description}
          </div>
        )}
      </div>

      <RadioGroup
        className="budget-category-view-options"
        disabled={isSelecting}
        name={`${budgetCategoryCode.toLowerCase()}-budget`}
        options={budgetCategoryOptions}
        selected={selected?.id}
        onChange={handleChoiceChange}
      >
        {renderOptionContent ?? defaultRenderOptionContent}
      </RadioGroup>
    </div>
  );
};

BudgetCategoryView.Prelude = ({ children }) => children;

BudgetCategoryView.propTypes = {
  budgetCategoryCode: PropTypes.string.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  getOptionAmount: PropTypes.func,
  isSelecting: PropTypes.bool,
  onChoiceChange: PropTypes.func,
  renderOptionContent: PropTypes.func,
  selected: PropTypes.shape({
    id: PropTypes.number,
  }),
};
